/**
 * EmirateDocsCopyBar — copy the required-documents set between emirates.
 *  • "نسخ من إمارة" → replaces the current emirate's docs with the chosen source.
 *  • "نسخ إلى إمارات أخرى" → pushes the current emirate's docs to selected targets.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Loader2, ArrowLeftRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { EMIRATES } from './serviceEmirates';
import { getAuthHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmirateDocsCopyBar({ serviceId, emirate, summary = {}, language, onCopied, testIdPrefix = 'svc',
  copyUrl, nounAr = 'الأوراق', nounEn = 'documents', unitAr = 'ورقة', unitEn = 'document(s)' }) {
  const isAr = language === 'ar';
  const url = copyUrl || `${API_URL}/api/services/${serviceId}/required-docs/copy`;
  const label = (k) => { const e = EMIRATES.find((x) => x.key === k); return e ? (isAr ? e.ar : e.en) : k; };
  const sources = EMIRATES.filter((e) => e.key !== emirate && (summary[e.key] || 0) > 0);
  const others = EMIRATES.filter((e) => e.key !== emirate);
  const currentCount = summary[emirate] || 0;

  const [fromKey, setFromKey] = useState('');
  const [confirmFrom, setConfirmFrom] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  const [toSel, setToSel] = useState([]);
  const [busy, setBusy] = useState(false);

  const copy = async (from_emirate, to_emirates) => {
    setBusy(true);
    try {
      const r = await axios.post(url, { from_emirate, to_emirates }, { headers: getAuthHeaders() });
      toast.success(isAr ? `تم نسخ ${r.data?.copied ?? 0} ${unitAr} إلى ${to_emirates.map(label).join('، ')}` : `Copied ${r.data?.copied ?? 0} ${unitEn} to ${to_emirates.map(label).join(', ')}`);
      onCopied?.(r.data?.summary);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل النسخ' : 'Copy failed'));
    } finally {
      setBusy(false);
      setConfirmFrom(false);
      setToOpen(false);
      setToSel([]);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 flex flex-wrap items-center gap-3" data-testid={`${testIdPrefix}-docs-copy-bar`}>
      <span className="text-xs text-white/60 flex items-center gap-1"><ArrowLeftRight className="h-3.5 w-3.5 text-[#c9a227]" />{isAr ? `نسخ ${nounAr}:` : `Copy ${nounEn}:`}</span>

      <div className="flex items-center gap-2">
        <select value={fromKey} onChange={(e) => setFromKey(e.target.value)} className="h-8 rounded-md bg-[#050505] border border-[#2a2a4e] text-white text-xs px-2" data-testid={`${testIdPrefix}-docs-copy-from-select`}>
          <option value="">{isAr ? 'من إمارة…' : 'From emirate…'}</option>
          {sources.map((e) => <option key={e.key} value={e.key}>{label(e.key)} ({summary[e.key]})</option>)}
        </select>
        <Button size="sm" variant="outline" disabled={!fromKey || busy} onClick={() => setConfirmFrom(true)} className="h-8 border-[#2a2a4e] bg-transparent text-white text-xs" data-testid={`${testIdPrefix}-docs-copy-from-btn`}>
          <Copy className="h-3.5 w-3.5 me-1" />{isAr ? `نسخ إلى ${label(emirate)}` : `Copy to ${label(emirate)}`}
        </Button>
        {sources.length === 0 && <span className="text-[11px] text-white/35">{isAr ? `لا توجد إمارة أخرى لديها ${nounAr} بعد` : `No other emirate has ${nounEn} yet`}</span>}
      </div>

      <Button size="sm" disabled={currentCount === 0 || busy} onClick={() => setToOpen(true)} className="h-8 bg-[#d4af37] hover:bg-[#c9a227] text-black text-xs ms-auto" data-testid={`${testIdPrefix}-docs-copy-to-btn`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin me-1" /> : <Copy className="h-3.5 w-3.5 me-1" />}
        {isAr ? `نسخ ${nounAr} ${label(emirate)} إلى إمارات أخرى` : `Copy ${label(emirate)} ${nounEn} to other emirates`}
      </Button>

      <AlertDialog open={confirmFrom} onOpenChange={(o) => !o && setConfirmFrom(false)}>
        <AlertDialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white" data-testid={`${testIdPrefix}-docs-copy-from-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{isAr ? `استبدال ${nounAr} هذه الإمارة؟` : `Replace this emirate's ${nounEn}?`}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/70">
              {isAr
                ? `سيتم استبدال ${nounAr} ${label(emirate)} الحالية (${currentCount}) بـ${nounAr} ${label(fromKey)} (${summary[fromKey] || 0}).`
                : `${label(emirate)}'s current ${nounEn} (${currentCount}) will be replaced with ${label(fromKey)}'s (${summary[fromKey] || 0}).`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="border-[#2a2a4e] bg-transparent text-white">{isAr ? 'إلغاء' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={() => copy(fromKey, [emirate])} className="bg-[#d4af37] hover:bg-[#c9a227] text-black" data-testid={`${testIdPrefix}-docs-copy-from-confirm`}>{isAr ? 'نعم، انسخ' : 'Yes, copy'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={toOpen} onOpenChange={(o) => { setToOpen(o); if (!o) setToSel([]); }}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-md" data-testid={`${testIdPrefix}-docs-copy-to-dialog`}>
          <DialogHeader>
            <DialogTitle>{isAr ? `نسخ ${nounAr} ${label(emirate)} (${currentCount}) إلى:` : `Copy ${label(emirate)} ${nounEn} (${currentCount}) to:`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer pb-2 border-b border-white/10">
              <Checkbox checked={toSel.length === others.length} onCheckedChange={(v) => setToSel(v ? others.map((e) => e.key) : [])} data-testid={`${testIdPrefix}-docs-copy-to-all`} />
              {isAr ? 'تحديد كل الإمارات' : 'Select all emirates'}
            </label>
            {others.map((e) => (
              <label key={e.key} className="flex items-center justify-between text-sm text-white cursor-pointer rounded-lg px-2 py-1.5 hover:bg-white/5">
                <span className="flex items-center gap-2">
                  <Checkbox checked={toSel.includes(e.key)} onCheckedChange={(v) => setToSel((s) => (v ? [...s, e.key] : s.filter((k) => k !== e.key)))} data-testid={`${testIdPrefix}-docs-copy-to-${e.key}`} />
                  {label(e.key)}
                </span>
                <span className="text-[11px] text-white/40">{isAr ? `حالياً ${summary[e.key] || 0}` : `now ${summary[e.key] || 0}`}</span>
              </label>
            ))}
            <p className="text-[11px] text-amber-300/80">{isAr ? `سيتم استبدال ${nounAr} الإمارات المحددة بالكامل.` : `Selected emirates' ${nounEn} will be fully replaced.`}</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setToOpen(false)} className="border-[#2a2a4e] bg-transparent text-white">{isAr ? 'إلغاء' : 'Cancel'}</Button>
            <Button disabled={!toSel.length || busy} onClick={() => copy(emirate, toSel)} className="bg-[#d4af37] hover:bg-[#c9a227] text-black" data-testid={`${testIdPrefix}-docs-copy-to-confirm`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : <Copy className="h-4 w-4 me-1" />}{isAr ? `نسخ إلى ${toSel.length} إمارة` : `Copy to ${toSel.length} emirate(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
